<script setup>
import ConfirmDeleteModal from "@/app/components/modal/ConfirmDeleteModal.vue"
import SessionLimitModal from "@/app/components/modal/SessionLimitModal.vue"
import BaseButton from "@/app/components/ui/BaseButton.vue"
import BaseIcon from "@/app/components/ui/BaseIcon.vue"
import { useI18n } from "@/app/composables/useI18n"
import { toast } from "@/app/utils/notification"
import { computed, onMounted, ref } from "vue"
import { deleteSessionLimit, getSessionLimits, saveSessionLimit } from "./api/index.js"

const { t } = useI18n()

// Modal state
const showModal = ref(false)
const isEditMode = ref(false)
const currentLimit = ref({})

// Confirmation modal state
const showConfirmModal = ref(false)
const limitToDelete = ref(null)

// Session limits data and loading state
const limits = ref([])
const isLoading = ref(false)

// Computed properties for organizing limits
const activeLimits = computed(() =>
  limits.value.filter(limit => limit.status === "active"),
)

const pendingLimits = computed(() =>
  limits.value.filter(limit => limit.status === "pending"),
)

const scheduledForDeletion = computed(() =>
  limits.value.filter(limit => limit.status === "scheduled_for_delete"),
)

// Functions
async function loadSessionLimits() {
  try {
    isLoading.value = true
    const response = await getSessionLimits()
    limits.value = response.data.data || []
  }
  catch (error) {
    console.error("Failed to load session limits:", error)
    toast.error(t("responsible_gambling.failed_to_load_limits") || "Failed to load session limits")
    limits.value = []
  }
  finally {
    isLoading.value = false
  }
}

function openCreateModal() {
  isEditMode.value = false
  currentLimit.value = {}
  showModal.value = true
}

function openEditModal(limit) {
  isEditMode.value = true
  currentLimit.value = limit
  showModal.value = true
}

function closeModal() {
  showModal.value = false
  currentLimit.value = {}
}

async function handleSaveLimit(formData) {
  try {
    isLoading.value = true
    const data = {
      type: formData.type,
      period: formData.period,
      value: formData.value,
    }

    // For updates, include the ID
    if (isEditMode.value && currentLimit.value.id) {
      data.id = currentLimit.value.id
    }

    const response = await saveSessionLimit(data)

    // Show appropriate message based on response
    if (response.data.status === "pending") {
      toast.info(t("responsible_gambling.limit_increase_24h") || "Limit increase will take effect in 24 hours")
    }
    else if (response.data.status === "active") {
      toast.info(t("responsible_gambling.limit_updated") || "Limit updated successfully")
    }
    else {
      toast.info(isEditMode.value ? (t("responsible_gambling.limit_updated") || "Limit updated successfully") : (t("responsible_gambling.limit_created") || "Limit created successfully"))
    }

    // Reload limits after save
    await loadSessionLimits()
  }
  catch (error) {
    console.error("Failed to save limit:", error)
    if (error.response?.status === 422) {
      toast.error(error.response.data.message || (t("responsible_gambling.validation_error") || "Validation error"))
    }
    else {
      toast.error(t("responsible_gambling.failed_to_save_limit") || "Failed to save limit")
    }
  }
  finally {
    isLoading.value = false
  }
}

function deleteLimit(limitId) {
  const limit = limits.value.find(l => l.id === limitId)
  if (limit) {
    limitToDelete.value = limit
    showConfirmModal.value = true
  }
}

async function confirmDelete() {
  if (limitToDelete.value) {
    try {
      isLoading.value = true
      await deleteSessionLimit(limitToDelete.value.id)

      // Reload limits after delete
      await loadSessionLimits()

      toast.info(t("responsible_gambling.limit_removal_24h") || "Limit will be removed in 24 hours")
      limitToDelete.value = null
    }
    catch (error) {
      console.error("Failed to delete limit:", error)
      toast.error(t("responsible_gambling.failed_to_delete_limit") || "Failed to delete limit")
    }
    finally {
      isLoading.value = false
    }
  }
}

function closeConfirmModal() {
  showConfirmModal.value = false
  limitToDelete.value = null
}

// Formatting functions for display
function formatLimitType(type) {
  const typeMap = {
    session: t("responsible_gambling.session") || "Session",
    bet: t("responsible_gambling.bet") || "Bet",
    deposit: t("responsible_gambling.deposit") || "Deposit",
  }
  return typeMap[type] || type
}

function formatLimitDescription(limit) {
  const unit = limit.type === "session" ? "min" : "USD"
  return `${limit.value} ${unit} per ${limit.period}`
}

function formatDateTime(dateTimeString) {
  if (!dateTimeString)
    return ""
  const date = new Date(dateTimeString)
  return date.toLocaleString()
}

function getStatusText(limit) {
  switch (limit.status) {
    case "active":
      return t("responsible_gambling.active") || "Active"
    case "pending":
      return t("responsible_gambling.pending") || "Pending"
    case "scheduled_for_delete":
      return t("responsible_gambling.scheduled_for_deletion") || "Scheduled for deletion"
    default:
      return t("responsible_gambling.unknown") || "Unknown"
  }
}

function getStatusClass(limit) {
  switch (limit.status) {
    case "active":
      return "status-active"
    case "pending":
      return "status-pending"
    case "scheduled_for_delete":
      return "status-deletion"
    default:
      return "status-unknown"
  }
}

function getTimingText(limit) {
  if (limit.status === "pending" && limit.activation_time) {
    return `${t("responsible_gambling.will_be_active") || "Will be active"}: ${formatDateTime(limit.activation_time)}`
  }
  if (limit.status === "scheduled_for_delete" && limit.deletion_time) {
    return `${t("responsible_gambling.will_be_deleted") || "Will be deleted"}: ${formatDateTime(limit.deletion_time)}`
  }
  if (limit.status === "active" && limit.expiration_time) {
    return `${t("responsible_gambling.will_expire") || "Will expire"}: ${formatDateTime(limit.expiration_time)}`
  }
  return ""
}

onMounted(async () => {
  // Load session limits from API
  await loadSessionLimits()
})
</script>

<template>
  <div class="container">
    <div class="form">
      <div class="title">
        {{ t("responsible_gambling.title") || "Responsible gambling limits" }}
      </div>
      <div class="subtitle">
        {{ t("responsible_gambling.subtitle") || "Here you can apply bet, session or deposit limits." }}
      </div>

      <div v-if="isLoading" class="loading">
        <p>{{ t("finance.loading") || "Loading..." }}</p>
      </div>

      <div v-else-if="limits.length === 0" class="no-limits">
        <p>{{ t("account.no_limits_set") || "No limits have been set yet." }}</p>
      </div>

      <div v-else class="limits-list">
        <!-- Active Limits -->
        <div v-if="activeLimits.length > 0" class="limits-section">
          <div v-for="limit in activeLimits" :key="limit.id" class="limit-item">
            <div class="limit-info">
              <div class="limit-header">
                <div class="limit-name">
                  {{ formatLimitType(limit.type) }}
                </div>
                <div class="limit-status" :class="getStatusClass(limit)">
                  {{ getStatusText(limit) }}
                </div>
              </div>
              <div class="limit-description">
                {{ formatLimitDescription(limit) }}
              </div>
              <div v-if="getTimingText(limit)" class="limit-timing">
                {{ getTimingText(limit) }}
              </div>
            </div>

            <div class="limit-actions">
              <div class="action-buttons">
                <BaseButton
                  class="edit-btn icon-btn"
                  :disabled="isLoading"
                  @click="openEditModal(limit)"
                >
                  <BaseIcon name="edit-info" size="16" />
                </BaseButton>
                <BaseButton
                  class="delete-btn icon-btn"
                  :disabled="isLoading"
                  @click="deleteLimit(limit.id)"
                >
                  <BaseIcon name="close" size="16" color="#ffffff" />
                </BaseButton>
              </div>
            </div>
          </div>
        </div>

        <!-- Pending Limits -->
        <div v-if="pendingLimits.length > 0" class="limits-section">
          <div v-for="limit in pendingLimits" :key="limit.id" class="limit-item">
            <div class="limit-info">
              <div class="limit-header">
                <div class="limit-name">
                  {{ formatLimitType(limit.type) }}
                </div>
                <div class="limit-status" :class="getStatusClass(limit)">
                  {{ getStatusText(limit) }}
                </div>
              </div>
              <div class="limit-description">
                {{ formatLimitDescription(limit) }}
              </div>
              <div v-if="getTimingText(limit)" class="limit-timing">
                {{ getTimingText(limit) }}
              </div>
            </div>

            <div class="limit-actions">
              <div class="action-buttons">
                <BaseButton
                  class="edit-btn icon-btn"
                  :disabled="isLoading"
                  @click="openEditModal(limit)"
                >
                  <BaseIcon name="edit-info" size="16" />
                </BaseButton>
              </div>
            </div>
          </div>
        </div>

        <!-- Scheduled for Deletion -->
        <div v-if="scheduledForDeletion.length > 0" class="limits-section">
          <div v-for="limit in scheduledForDeletion" :key="limit.id" class="limit-item">
            <div class="limit-info">
              <div class="limit-header">
                <div class="limit-name">
                  {{ formatLimitType(limit.type) }}
                </div>
                <div class="limit-status" :class="getStatusClass(limit)">
                  {{ getStatusText(limit) }}
                </div>
              </div>
              <div class="limit-description">
                {{ formatLimitDescription(limit) }}
              </div>
              <div v-if="getTimingText(limit)" class="limit-timing">
                {{ getTimingText(limit) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <BaseButton
        class="create-btn alight wide high"
        :disabled="isLoading"
        @click="openCreateModal()"
      >
        {{ t("common.create") || "Create" }}
      </BaseButton>
    </div>

    <!-- Create/Edit Modal -->
    <SessionLimitModal
      :show="showModal"
      :edit-mode="isEditMode"
      :limit-data="currentLimit"
      @close="closeModal"
      @save="handleSaveLimit"
    />

    <!-- Confirmation Modal -->
    <ConfirmDeleteModal
      :show="showConfirmModal"
      :item="limitToDelete"
      @close="closeConfirmModal"
      @confirm="confirmDelete"
    />
  </div>
</template>

<style lang="scss" scoped>
.container {
  .form {
    border-radius: 8px;
    background: var(--be-big-card-bg);
    padding: 16px;
    margin-bottom: 1rem;

    .title {
      font-size: 16px;
      font-weight: 600;
      color: var(--be-primary-text-color);
    }

    .subtitle {
      margin-top: 10px;
      font-size: 14px;
      font-weight: 400;
      color: var(--be-primary-text-color);
    }

    .no-limits, .loading {
      text-align: center;
      padding: 20px;
      color: var(--be-text-secondary);
      font-style: italic;
    }

    .limits-list {
      margin-top: 10px;
      margin-bottom: 20px;

      .limits-section {
        margin-bottom: 24px;

        .section-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--be-primary-text-color);
          margin-bottom: 12px;
          padding-bottom: 6px;
          border-bottom: 1px solid var(--be-border-color);
        }
      }

      .limit-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px;
        margin-bottom: 12px;
        border-radius: 8px;
        background: var(--be-small-card-bg);
        border: 1px solid var(--be-border-color);

        .limit-info {
          flex: 1;

          .limit-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 4px;

          .limit-name {
            font-size: 16px;
            font-weight: 600;
            color: var(--be-primary-text-color);
            }

            .limit-status {
              font-size: 10px;
              font-weight: 500;
              padding: 2px 8px;
              border-radius: 12px;
              text-transform: uppercase;

              &.status-active {
                background: #4caf50;
                color: white;
              }

              &.status-pending {
                background: #ff9800;
                color: white;
              }

              &.status-deletion {
                background: #f44336;
                color: white;
              }

              &.status-unknown {
                background: #9e9e9e;
                color: white;
              }
            }
          }

          .limit-description {
            font-size: 14px;
            color: var(--be-text-secondary);
            margin-bottom: 4px;
          }

          .limit-timing {
            font-size: 12px;
            color: var(--be-text-secondary);
            font-style: italic;
          }
        }

                .limit-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;

          .action-buttons {
            display: flex;
            gap: 8px;

            .icon-btn {
              min-height: 24px;
              min-width: 24px;
              max-width: 24px;
              max-height: 24px;
              padding: 0;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;

              &.edit-btn {
                background: var(--be-primary-btn);

                &:hover {
                  background: var(--be-primary-btn-hover, #2c6bc4);
                }
              }

              &.delete-btn {
                background: #f44336;

                &:hover {
                  background: #d32f2f;
                }
              }
            }
          }
        }
      }
    }

    .create-btn {
      margin-top: 16px;
    }
  }
}

@media screen and (max-width: 768px) {
  .limits-list {
    .limit-item {
      flex-direction: row;
      align-items: flex-start;
      gap: 16px;
      padding: 20px 16px;

      .limit-actions {
        width: 100%;
        justify-content: flex-end;

        .action-buttons {
          gap: 12px;

          .icon-btn {
            min-height: 44px;
            min-width: 44px;
            max-width: 44px;
            max-height: 44px;
            border-radius: 8px;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 500px) {
  .container {
    height: 100%;

    .form {
      padding: 12px;
    }
  }

  .limits-list {
    .limit-item {
      padding: 16px 12px;

      .limit-actions {
        .action-buttons {
          gap: 8px;

          .icon-btn {
            min-height: 40px;
            min-width: 40px;
            max-width: 40px;
            max-height: 40px;
            border-radius: 6px;
          }
        }
      }
    }
  }

  .create-btn {
    padding: 16px;
    font-size: 16px;
    min-height: 48px;
  }
}
</style>
